import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../../components/LegalPage';
import legalConfig from '../../lib/legalConfig';
import { useLanguage } from '../../context/LanguageContext';

const META_DESC =
  'CosmoEstates Privacy Policy: what personal data we collect, why we use it, who we share it with (including agents and contractors via lead forms), how long we keep it, and your GDPR rights.';

export default function PrivacyPage() {
  const { t } = useLanguage();
  return (
    <LegalPage
      title={t('legal.docs.privacyTitle')}
      effectiveDate={legalConfig.effectiveDate}
      metaTitle="Privacy Policy | CosmoEstates"
      metaDescription={META_DESC}
    >
      <div className="legal-meta">
        <p>
          <strong>Controller:</strong> {legalConfig.entityName} ("CosmoEstates", "we", "us"),
          registered in the Republic of Cyprus under number{' '}
          {legalConfig.companyRegistrationNumber}, registered office{' '}
          {legalConfig.registeredAddress}.
        </p>
        <p>
          <strong>Website:</strong>{' '}
          <a href={legalConfig.websiteUrl}>{legalConfig.websiteUrl}</a>
        </p>
        <p>
          <strong>Privacy contact:</strong> {legalConfig.privacyEmail}
        </p>
      </div>

      <p className="legal-draft-note">
        Draft for review by Cyprus-qualified counsel. Not legal advice. This policy is written
        to align with the EU General Data Protection Regulation (GDPR, Regulation (EU)
        2016/679) and the Cyprus Law 125(I)/2018.
      </p>

      <h2>1. Who we are</h2>
      <p>
        CosmoEstates is the data controller for the personal data described in this policy. If
        you have any questions or want to exercise your rights, contact us at{' '}
        {legalConfig.privacyEmail}.
      </p>
      <div className="legal-callout">
        <p>
          <strong>[Decide &amp; fill in]</strong> If you appoint a Data Protection Officer (not
          always required), add their contact details here.
        </p>
      </div>

      <h2>2. Scope</h2>
      <p>
        This policy explains what personal data we collect when you use the Platform, why we use
        it, who we share it with, how long we keep it, and your rights. It applies to visitors,
        registered users, professional users (agents, partners, contractors), and people whose
        details are submitted through our lead forms.
      </p>

      <h2>3. The personal data we collect</h2>
      <p>
        <strong>You give us:</strong>
      </p>
      <ul>
        <li>
          <strong>Account data</strong> — name, email, phone, password, user type (private /
          agent / partner / contractor).
        </li>
        <li>
          <strong>Listing data</strong> — property details, addresses, prices, photos, and
          descriptions you upload.
        </li>
        <li>
          <strong>Lead-form data</strong> — when you use <strong>Find Agent</strong>,{' '}
          <strong>Find Technician</strong>, the <strong>Property Price Estimate</strong>{' '}
          follow-up, or similar forms: your name, email, district/location, and the details of
          what you are looking for (e.g. reason for selling, timeframe, budget, renovation
          needs).
        </li>
        <li>
          <strong>Messages</strong> — content of messages you send through the Platform inbox
          and to us.
        </li>
        <li>
          <strong>Payment-related data</strong> — for paid features (Pro, Boost, professional
          fees). Card details are processed by our payment provider; we do not store full card
          numbers. See section 7.
        </li>
      </ul>

      <p>
        <strong>We collect automatically:</strong>
      </p>
      <ul>
        <li>
          <strong>Usage and device data</strong> — IP address, browser, device, pages viewed,
          referring site, and interactions (including which listings you view, to power
          "recently viewed" and "similar to what you viewed" features).
        </li>
        <li>
          <strong>Cookies and local storage</strong> — including <code>localStorage</code> and{' '}
          <code>sessionStorage</code>. See our <Link to="/cookies">Cookie Policy</Link>.
        </li>
      </ul>

      <p>
        <strong>From third parties / public sources:</strong>
      </p>
      <ul>
        <li>
          For features such as <strong>Neighbourhood Intelligence</strong>, we use information
          sourced from public data (e.g. transport, schools, amenities, price indices). This is
          generally not personal data about you, but is described here for transparency.
        </li>
      </ul>

      <div className="legal-callout">
        <p>
          ⚠️ <strong>Browser-history note for your developers.</strong> Your spec says "recently
          viewed" history in <code>localStorage</code> is <strong>not cleared on logout</strong>,
          so the next anonymous user on the same browser can inherit the previous user's
          history. This is a privacy risk you should fix (clear local viewing history on logout)
          and, until fixed, you should be transparent about it. Discuss with your
          lawyer/developer.
        </p>
      </div>

      <h2>4. Why we use your data and our legal basis</h2>
      <div className="legal-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Purpose</th>
              <th>Legal basis (GDPR Art. 6)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Create and manage your account; provide the Platform</td>
              <td>Performance of a contract</td>
            </tr>
            <tr>
              <td>Publish and display your listings</td>
              <td>Performance of a contract</td>
            </tr>
            <tr>
              <td>Connect you with agents/contractors via lead forms (CPL)</td>
              <td>
                Consent (you choose to submit the form) and/or our legitimate interest in
                operating the marketplace
              </td>
            </tr>
            <tr>
              <td>Process payments for paid features</td>
              <td>Performance of a contract; legal obligation (accounting)</td>
            </tr>
            <tr>
              <td>
                Send service emails (e.g. price-drop alerts, similar-listing alerts you opted
                into)
              </td>
              <td>Performance of a contract / consent</td>
            </tr>
            <tr>
              <td>Send marketing emails</td>
              <td>Consent (you can withdraw at any time)</td>
            </tr>
            <tr>
              <td>Personalisation ("recently viewed", "similar to what you viewed")</td>
              <td>Legitimate interest and/or consent via cookies</td>
            </tr>
            <tr>
              <td>
                Detect and prevent fraud, scams, duplicate or abusive listings; ensure security
              </td>
              <td>Legitimate interest; legal obligation</td>
            </tr>
            <tr>
              <td>Comply with legal obligations and respond to authorities</td>
              <td>Legal obligation</td>
            </tr>
            <tr>
              <td>Analytics to improve the Platform</td>
              <td>Consent (where cookie-based) / legitimate interest</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        Where we rely on <strong>legitimate interests</strong>, you can object — see section 9.
      </p>

      <h2>
        5. Lead generation — sharing your data with agents and contractors (important)
      </h2>
      <p>
        5.1 When you submit a lead form (for example <strong>Find Agent</strong>,{' '}
        <strong>Find Technician</strong>, or a <strong>Property Price Estimate</strong>{' '}
        enquiry), the purpose of that form is to put you in contact with third-party
        professionals ("<strong>Providers</strong>") — for example up to three matching estate
        agents, or contractors/technicians ready to take on the work.
      </p>
      <p>
        5.2{' '}
        <strong>
          This means we will share the personal data you submit (such as your name, email,
          district, and enquiry details) with one or more Providers, including on a paid
          cost-per-lead basis.
        </strong>{' '}
        Those Providers then contact you directly. We share only what is relevant to the
        enquiry.
      </p>
      <p>
        5.3 Each Provider becomes an <strong>independent controller</strong> of your data once
        it is shared with them and is responsible for how it then uses your data, under its own
        privacy policy. We require Providers to use the data only to respond to your enquiry
        and in compliance with data protection law, but we do not control their subsequent use.
      </p>
      <p>
        5.4 If you do not want your details shared with Providers,{' '}
        <strong>do not submit these forms.</strong> You can still use the rest of the Platform.
      </p>

      <h2>6. Marketing communications</h2>
      <p>
        If you opt in (e.g. to price-drop alerts, similar-listing alerts, or newsletters), we
        will send you those messages. You can unsubscribe at any time via the link in each email
        or by contacting {legalConfig.privacyEmail}. Electronic marketing is also subject to
        Cyprus rules on electronic communications.
      </p>

      <h2>7. Service providers (processors) we use</h2>
      <p>
        We use trusted third parties to run the Platform. They process data on our instructions
        under data-processing agreements. These include:
      </p>
      <ul>
        <li>
          <strong>Hosting &amp; database</strong> — Supabase (database, authentication,
          real-time messaging, file storage).
        </li>
        <li>
          <strong>Email delivery</strong> — Resend (transactional emails such as account,
          alerts, and notifications).
        </li>
        <li>
          <strong>Payments</strong> — Stripe (subscription and Boost checkout, card processing).
        </li>
        <li>
          <strong>Analytics</strong> — we do not use a third-party analytics provider. The
          neighbourhood and property-index statistics shown on the Platform come from public
          Cyprus government data sources and are not personal data about you.
        </li>
        <li>
          <strong>AI / estimate tooling</strong> — Anthropic (Claude API) for the Property
          Price Estimate, Renovation cost tool, Neighbourhood Intelligence summaries, and the
          in-app support chat; Stability AI for room-redesign images. Inputs you submit to
          these tools are sent to those providers solely to generate the requested output.
        </li>
      </ul>

      <h2>8. International transfers</h2>
      <p>
        Some providers may process data outside the European Economic Area. Where they do, we
        rely on appropriate safeguards under the GDPR (for example European Commission adequacy
        decisions or Standard Contractual Clauses). Contact {legalConfig.privacyEmail} for
        details.
      </p>

      <h2>9. Your rights</h2>
      <p>
        Under the GDPR and Law 125(I)/2018 you have the right to: access your data; rectify
        inaccurate data; erase data ("right to be forgotten"); restrict or object to processing;
        data portability; and to withdraw consent at any time (without affecting prior
        processing). Where we rely on legitimate interests, you can object to that processing.
      </p>
      <p>
        To exercise any right, contact {legalConfig.privacyEmail}. We will respond within the
        time limits set by the GDPR (generally one month).
      </p>
      <p>
        You also have the right to lodge a complaint with the Cyprus supervisory authority:{' '}
        <strong>Commissioner for Personal Data Protection</strong> (Office of the Commissioner
        for Personal Data Protection, Cyprus) — see{' '}
        <a
          href="https://www.dataprotection.gov.cy"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://www.dataprotection.gov.cy
        </a>
        .
      </p>

      <h2>10. How long we keep data</h2>
      <p>We keep personal data only as long as necessary for the purposes above:</p>
      <ul>
        <li>
          <strong>Account data</strong> — while your account is active and for{' '}
          <strong>30 days</strong> after closure (to handle reactivation requests and
          deletion-related queries), after which it is deleted or anonymised.
        </li>
        <li>
          <strong>Listings</strong> — while published and for <strong>12 months</strong> after
          removal or expiry, after which the listing record is deleted or anonymised.
        </li>
        <li>
          <strong>Lead-form data</strong> — for <strong>1 month</strong> after submission, plus
          a record of the fact that a lead was shared (without the lead details themselves) for
          our internal accounting and audit purposes.
        </li>
        <li>
          <strong>Payment/accounting records</strong> — for <strong>7 years</strong>, as
          required by Cyprus tax and accounting law.
        </li>
        <li>
          <strong>Viewing-history data</strong> — <code>localStorage</code> entries clear after
          30 days; database rows linked to your account are kept for up to{' '}
          <strong>6 months</strong>, with the most recent ~10 listings retained per user to
          power the "recently viewed" and "similar to what you viewed" features.
        </li>
      </ul>

      <h2>11. Security</h2>
      <p>
        We take reasonable technical and organisational measures to protect personal data
        against loss, misuse, and unauthorised access. No system is completely secure; if a
        personal data breach occurs that is likely to cause a risk to you, we will notify the
        Commissioner and, where required, you, in line with the GDPR.
      </p>

      <h2>12. Children</h2>
      <p>
        The Platform is not intended for children. We do not knowingly collect data from anyone
        under 18. Under Law 125(I)/2018 the age of consent for information-society services in
        Cyprus is 13; regardless, our Services require users to be 18+.
      </p>

      <h2>13. Changes to this policy</h2>
      <p>
        We may update this policy. We will post the new version with an updated effective date
        and, for material changes, take reasonable steps to notify you.
      </p>

      <h2>14. Contact</h2>
      <p>
        Questions or requests: <strong>{legalConfig.privacyEmail}</strong>.
      </p>
    </LegalPage>
  );
}
